import { FC, useEffect, useMemo, useState } from 'react'
import { Group } from '../../components/Group'
import { Group as IGroup, Item, SubGroup as ISubGroup } from '../../types'
import { SearchField } from 'components/form/search-field'
import { findItem, includes, itemsFromTasks } from '../../helpers'
import { useAssignedTasksQuery } from '@features/time-logging/hooks/useTasks'
import { Spinner } from 'components/loaders'
import { useUserId } from '@features/time-logging/hooks/useUserId'
import { useSet } from '@hooks/useSet'
import { IconButton, MenuButton } from 'components/buttons'
import { faSquareMinus, faSquarePlus } from '@fortawesome/pro-regular-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { useTranslation } from 'react-i18next'
import { InfiniteScrollListItem } from 'components/containers/infinite-scroll-list'
import { ActiveButtonGroup } from './ActiveButtonGroup'
import { TaskActivity } from './taskActivity'

const parseLocalStorageResult = (
  result: ReturnType<Storage['getItem']>,
): number[] => {
  if (result === null) return []

  try {
    const parsed = JSON.parse(result) as number[]
    return parsed.every((i) => typeof i === 'number') ? parsed : []
  } catch {
    return []
  }
}

type ItemsByDisplayLabel = Record<string, Item[]>

const groupByDisplayLabel = (
  groupedRef: ItemsByDisplayLabel,
  item: Item,
): ItemsByDisplayLabel => {
  const grouped = { ...groupedRef }
  /**
   * The normalized display label is important for sorting, grouping, and
   * displaying the "display path".  I'm utilizing the "---" characters/delimeter
   * so that it can be easily split later in massageIntoSubGroups below.
   *
   * It's a bit smelly, I know, but it's a light touch to existing code.
   *
   * - AF 9/30/24
   */
  const normalizedDisplayLabel = [
    item.task.projectIntegration.code,
    ...item.task.displayPath,
  ].join('---')
  const itemsByDisplayLabel = grouped[normalizedDisplayLabel] ?? []
  grouped[normalizedDisplayLabel] = [...itemsByDisplayLabel, item]
  return grouped
}

const massageIntoSubGroups = (
  itemsByDisplayLabel: ItemsByDisplayLabel,
): ISubGroup[] => {
  return Object.entries(itemsByDisplayLabel).map(([displayLabel, items]) => ({
    displayPath: displayLabel.split('---'),
    items,
  }))
}

const grabItemsFromProjectAsSubGroup = (
  items: Item[],
  projectId: number,
): ISubGroup[] => {
  const itemsByDisplayLabel = items
    .filter((item) => item.task.project.id === projectId)
    .reduce(groupByDisplayLabel, {})

  return massageIntoSubGroups(itemsByDisplayLabel)
}

const massageIntoGroups = (items: Item[]): IGroup[] => {
  const projects = items.reduce<Task['project'][]>((acc, item) => {
    if (acc.find((project) => project.id === item.task.project.id)) return acc

    return [...acc, item.task.project]
  }, [])

  return projects
    .map((project) => ({
      id: project.id,
      name: project.name,
      subGroups: grabItemsFromProjectAsSubGroup(items, project.id).sort(
        (a, b) =>
          (a.displayPath?.at(0) ?? a.name ?? '') >
          (b.displayPath?.at(0) ?? b.name ?? '')
            ? 1
            : -1,
      ),
    }))
    .sort((projectA, projectB) =>
      projectA.name.toUpperCase() > projectB.name.toUpperCase() ? 1 : -1,
    )
}

const filterItems = (items: Item[] | undefined, search: string) => {
  if (items === undefined) return []

  const includesSearch = includes(search.toLowerCase().trim())

  return items.filter((item) => {
    if (includesSearch(item.task.name)) return true
    if (item.task.displayPath.some(includesSearch)) return true
    if (includesSearch(item.task.project.name)) return true
    if (includesSearch(item.task.id.toString())) return true

    return false
  })
}

interface Props {
  disabledTaskIds: Set<number>
  hidden: boolean
  onTaskClick: (task: SelectedTask, checked: boolean) => void
  selectedTaskIds: Set<number>
}

export const MyTasks: FC<Props> = ({
  disabledTaskIds,
  hidden,
  onTaskClick,
  selectedTaskIds,
}) => {
  const { t } = useTranslation()
  const [search, setSearch] = useState('')
  const [selectedActivity, setSelectedActivity] =
    useState<TaskActivity>('active')
  const tasksQuery = useAssignedTasksQuery({
    enabled: !hidden,
    active: { active: true, inactive: false, all: undefined }[selectedActivity],
  })
  const userId = useUserId()

  const {
    set: collapsedProjects,
    add: collapseProject,
    remove: expandProject,
  } = useSet<number>()

  const localStorageKey = useMemo(
    () => `collapsed-projects-${userId}`,
    [userId],
  )

  // Initialize collapsed project ids
  useEffect(() => {
    const result = localStorage.getItem(localStorageKey)
    const collapsed = parseLocalStorageResult(result)
    collapsed.forEach(collapseProject)
  }, [collapseProject, localStorageKey])

  // Store updated collapsed project ids
  useEffect(() => {
    const collapsedStr = JSON.stringify(Array.from(collapsedProjects))
    localStorage.setItem(localStorageKey, collapsedStr)
  }, [collapsedProjects, localStorageKey])

  const items: Item[] = useMemo(() => {
    if (tasksQuery.data === undefined) return []

    return itemsFromTasks(tasksQuery.data, disabledTaskIds, selectedTaskIds, {
      hideActive: selectedActivity !== 'all',
    })
  }, [tasksQuery.data, disabledTaskIds, selectedTaskIds, selectedActivity])

  const filteredItems = useMemo(
    () => filterItems(items, search),
    [items, search],
  )

  const groups = useMemo(() => {
    return massageIntoGroups(filteredItems)
  }, [filteredItems])

  const expandCollapseMenuItems = useMemo(() => {
    const someCollapsed = groups.some(({ id }) => collapsedProjects.has(id))
    const allCollapsed = groups.every(({ id }) => collapsedProjects.has(id))

    return [
      {
        content: (
          <>
            <FontAwesomeIcon icon={faSquarePlus} className="mr-1" />{' '}
            {t('features.timeLogging.taskSelection.expandAll')}
          </>
        ),
        onClick: () => groups.forEach(({ id }) => expandProject(id)),
        disabled: !someCollapsed,
      },
      {
        content: (
          <>
            <FontAwesomeIcon icon={faSquareMinus} className="mr-1" />{' '}
            {t('features.timeLogging.taskSelection.collapseAll')}
          </>
        ),
        onClick: () => groups.forEach(({ id }) => collapseProject(id)),
        disabled: allCollapsed,
      },
    ]
  }, [groups, collapsedProjects, expandProject, collapseProject, t])

  if (hidden) return null

  return (
    <div className="flex flex-col px-9">
      <div className="flex flex-row items-center flex-nowrap">
        <SearchField
          className="w-full"
          disabled={tasksQuery.isLoading}
          autoFocus={true}
          value={search}
          onChange={setSearch}
          placeholder={t('features.timeLogging.taskSelection.searchMyTasks')}
        />
        <MenuButton
          disabled={tasksQuery.isLoading}
          button={({ toggleMenuHidden }) => (
            <IconButton onClick={toggleMenuHidden} iconProps={{ size: 'lg' }} />
          )}
          className="ml-2"
          menuItems={expandCollapseMenuItems}
        />
      </div>
      <ActiveButtonGroup
        className="mt-4 max-w-full"
        selected={selectedActivity}
        onClick={setSelectedActivity}
      />
      {tasksQuery.isLoading ? (
        <div className="flex justify-center mt-4">
          <Spinner className="text-3xl" />
        </div>
      ) : (
        groups.map((group) => {
          const collapsed = collapsedProjects.has(group.id)
          let groupHeight = 20 // rough height of the project name

          if (!collapsed) {
            group.subGroups.forEach((subGroup) => {
              groupHeight += 20 // rough height of the subGroup name
              groupHeight += subGroup.items.length * 40 // rough height of the task item
            })
          }

          return (
            <InfiniteScrollListItem
              key={group.name}
              expectedItemHeight={groupHeight}
            >
              <Group
                collapsed={collapsed}
                name={group.name}
                onCollapse={() => collapseProject(group.id)}
                onExpand={() => expandProject(group.id)}
                onItemClick={(id, checked) => {
                  onTaskClick(findItem(items, id).task, checked)
                }}
                subGroups={group.subGroups}
              />
            </InfiniteScrollListItem>
          )
        })
      )}
    </div>
  )
}
